import React, { useState } from "react";
import { useEffect } from "react";
import {
	Box,
	Grid2,
	Paper,
	Typography,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from "@mui/material";
import { Helmet } from "react-helmet";
import ReactGA from "react-ga4";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GosellGardensLogo from "../../Images/GosellGardensLogo.png";
import wildPawsImg from "../../Images/Sponsorships/wild_paws.jpg";
import ighDaysImg from "../../Images/Sponsorships/igh_days.png";
import supabase from "../../supabaseClient";
import { formatHoursLine } from "../../utils/cstDateTime";

const DEFAULT_HOURS = {
	farm: [
		"Friday's: 2:00 PM - 6:00 PM",
		"Saturday's: 9:00 AM - 6:00 PM",
		"Sunday's: 10:00 AM - 4:00 PM",
	],
	farmstand: ["May 15 - October 31st, Daily: 8:00 AM - Dusk"],
	note: "Additional Hours posted each week, mostly evenings and weekends. Always by appointment as well, so please reach out if you'd like to visit outside of regular hours!",
};

const Home = () => {
	useEffect(() => {
		ReactGA.send({
			hitType: "pageview",
			page: "/coming-soon",
		});
	}, []);

	const [hoursLoading, setHoursLoading] = useState(true);
	const [useDefaultHours, setUseDefaultHours] = useState(true);
	const [dayHours, setDayHours] = useState([]);

	useEffect(() => {
		const fetchHours = async () => {
			const [{ data: settingsData }, { data: hoursData }] = await Promise.all([
				supabase
					.from("business_hours_settings")
					.select("use_default_hours")
					.eq("singleton", true)
					.maybeSingle(),
				supabase
					.from("business_hours")
					.select("id, hour_type, opens_at, closes_at")
					.order("opens_at", { ascending: true }),
			]);

			setUseDefaultHours(settingsData?.use_default_hours ?? true);
			setDayHours(hoursData || []);
			setHoursLoading(false);
		};

		fetchHours();
	}, []);

	const [expanded, setExpanded] = useState("2026");

	const handleAccordionChange = (panel) => (event, isExpanded) => {
		setExpanded(isExpanded ? panel : false);
	};

	const farmDayHours = dayHours
		.filter((hour) => hour.hour_type === "farm")
		.map((hour) => formatHoursLine(hour.opens_at, hour.closes_at))
		.filter(Boolean);
	const farmstandDayHours = dayHours
		.filter((hour) => hour.hour_type === "farmstand")
		.map((hour) => formatHoursLine(hour.opens_at, hour.closes_at))
		.filter(Boolean);

	const farmHoursToDisplay = [
		...(useDefaultHours ? DEFAULT_HOURS.farm : []),
		...farmDayHours,
	];
	const farmstandHoursToDisplay = [
		...(useDefaultHours ? DEFAULT_HOURS.farmstand : []),
		...farmstandDayHours,
	];
	const noteToDisplay = useDefaultHours ? DEFAULT_HOURS.note : "";

	// Replace both mobile and desktop accordions with this shared content
	function HoursAccordion({ expanded, handleAccordionChange }) {
		return (
			<Accordion
				expanded={expanded === "2026"}
				onChange={handleAccordionChange("2026")}
				sx={{ mb: 2, boxShadow: 8, borderRadius: 2 }}
			>
				<AccordionSummary expandIcon={<ExpandMoreIcon />}>
					<Typography variant="h4" paddingLeft={2}>
						2026 Hours
					</Typography>
				</AccordionSummary>
				<AccordionDetails>
					<Paper
						elevation={0}
						sx={{ padding: 2, boxShadow: "none", borderRadius: 2 }}
					>
						<Typography variant="h5" sx={{ mb: 2 }}>
							Farm and Greenhouse Hours
						</Typography>
						<Typography variant="body2" sx={{ mb: 2 }}>
							{hoursLoading ? (
								<span>Loading hours...</span>
							) : farmHoursToDisplay.length === 0 ? (
								<span>No hours listed. Hours will be posted soon.</span>
							) : (
								farmHoursToDisplay.map((line, idx) => (
									<span key={idx}>
										{line}
										<br />
									</span>
								))
							)}
						</Typography>
						{!!noteToDisplay && (
							<Typography variant="body1" sx={{ mb: 2 }}>
								{noteToDisplay}
							</Typography>
						)}
					</Paper>
					<Paper
						elevation={0}
						sx={{
							padding: 2,

							boxShadow: "none",
							borderRadius: 2,
						}}
					>
						<Typography variant="h5" sx={{ mb: 2 }}>
							Farmstand Hours
						</Typography>
						<Typography variant="body1" sx={{ mb: 2 }}>
							{hoursLoading ? (
								<span>Loading hours...</span>
							) : farmstandHoursToDisplay.length === 0 ? (
								<span>No hours listed. Hours will be posted soon.</span>
							) : (
								farmstandHoursToDisplay.map((line, idx) => (
									<span key={idx}>
										{line}
										<br />
									</span>
								))
							)}
						</Typography>
					</Paper>
				</AccordionDetails>
			</Accordion>
		);
	}

	return (
		<Box>
			<Helmet>
				<meta
					name="description"
					content="Gosell Gardens is a family-owned farm / backyard nursery located in Inver Grove Heights, MN. We offer a wide variety of annuals, perennials, trees, shrubs and produce for sale. Our farm is open to the public during the growing season."
				/>
				<meta name="title" content="Gosell Gardens"></meta>
				<meta property="og:title" content="Gosell Gardens" />
				<meta
					property="og:description"
					content="Gosell Gardens is a family-owned farm / backyard nursery located in Inver Grove Heights, MN. We offer a wide variety of annuals, perennials, trees, shrubs and produce for sale. Our farm is open to the public during the growing season."
				/>
				<meta property="og:url" content="https://gosellgardens.com" />
				<meta property="og:type" content="website" />
				<title>Gosell Gardens</title>
				<link rel="canonical" href="https://gosellgardens.com" />
			</Helmet>

			<Box padding={1}>
				<Typography
					variant="h1"
					align="center"
					sx={{
						color: "primary",
						fontWeight: "bold",
						fontSize: "2.5rem",
						mt: 2,
					}}
				>
					Welcome to Gosell Gardens: Your friendly neighborhood farm, orchard,
					and backyard nursery!
				</Typography>
				<Typography variant="h2" align="center" sx={{ fontSize: "1rem" }}>
					We are a family-owned farm located in Inver Grove Heights, MN. We
					offer a wide variety of annuals, perennials, trees, shrubs and produce
					for sale. We also sell both chicken and duck eggs. Our farm is open to
					the public for regular hours during the summer growing season. We look
					forward to seeing you soon!
				</Typography>
			</Box>
			<Box padding={2}>
				<Grid2 container spacing={2} sx={{ mt: 4 }}>
					<Grid2 size={{ xs: 12, md: 6 }}>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								justifyContent: "left",
								alignItems: "left",
								padding: "2px",
							}}
						>
							{/* Google Map */}
							<iframe
								title="Farm Location"
								src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d2830.2489868642356!2d-93.0336870872974!3d44.81649196632605!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x87f7d3840216f02f%3A0x58efadaf4ed7a063!2sGosell%20Gardens!5e0!3m2!1sen!2sus!4v1742910248308!5m2!1sen!2sus"
								width="100%"
								height="400"
								style={{ border: 0 }}
								allowFullScreen=""
								loading="lazy"
							></iframe>
						</Box>
						<Box sx={{ mt: 2 }}>
							<Typography variant="h6" sx={{ mb: 2 }}>
								Contact Us:
							</Typography>
							<Typography variant="body1" sx={{ mb: 2 }}>
								Phone: 651-239-4444
							</Typography>
							<Typography variant="body1" sx={{ mb: 2 }}>
								Email: connor@gosellgardens.com (Connor - Owner, Plants)
							</Typography>
							<Typography variant="body1" sx={{ mb: 2 }}>
								Email: emily@gosellgardens.com (Emily - Owner, Animals)
							</Typography>
							<Typography variant="body1" sx={{ mb: 2 }}>
								Looking for employment? Visit our{" "}
								<a href="/employment" style={{ color: "blue" }}>
									Employment Page
								</a>{" "}
								to see current openings and apply!
							</Typography>
						</Box>
						{/* Hours accordion for all screens, responsive display */}
						<Box sx={{ mt: 2 }}>
							<Box sx={{ display: { xs: "block", md: "none" } }}>
								<HoursAccordion
									expanded={expanded}
									handleAccordionChange={handleAccordionChange}
								/>
							</Box>
						</Box>
						{/* Logo always at the bottom of the left column */}
						<Grid2
							container
							spacing={2}
							sx={{ mt: 2, mb: 4, alignItems: "center" }}
						>
							{/* First image always first on all screens */}
							<Grid2 item xs={12} sm={4} textAlign="center" p={1}>
								<img
									src={GosellGardensLogo}
									alt="Gosell Gardens Logo"
									style={{ width: "100%", maxWidth: 300 }}
									loading="lazy"
								/>
							</Grid2>
							{/* On mobile, show heading before the next two images; on desktop, above the next two images */}
							<Grid2 item xs={12} sm={8}>
								<Box
									sx={{
										display: { xs: "block", md: "flex" },
										flexDirection: "column",
										alignItems: { md: "center" },
										height: "100%",
									}}
								>
									<Typography
										variant="h5"
										sx={{
											alignSelf: { xs: "center", md: "flex-start" },
											ml: { xs: 0, md: 2 },
											mr: { xs: 0, md: 2 },
											mb: { xs: 2, md: 2 },
										}}
									>
										Proud Sponsor of:
									</Typography>
									<Grid2 container spacing={2}>
										<Grid2 item xs={6} md={6} textAlign="center" p={1}>
											<img
												onClick={() =>
													window.open("https://www.wildpaws.org/", "_blank")
												}
												src={wildPawsImg}
												alt="Wild Paws Logo"
												loading="lazy"
												style={{
													width: "100%",
													maxWidth: 300,
													cursor: "pointer",
												}}
											/>
										</Grid2>
										<Grid2 item xs={6} md={6} textAlign="center" p={1}>
											<img
												onClick={() =>
													window.open(
														"https://www.invergroveheightsdays.org",
														"_blank",
													)
												}
												src={ighDaysImg}
												alt="Inver Grove Heights Days Logo"
												loading="lazy"
												style={{
													width: "100%",
													maxWidth: 300,
													cursor: "pointer",
												}}
											/>
										</Grid2>
										{/* Add a third image here if needed, or leave as is for two */}
									</Grid2>
								</Box>
							</Grid2>
						</Grid2>
					</Grid2>
					<Grid2 size={{ xs: 12, md: 6 }}>
						{/* Hours accordions for desktop only */}
						<Box sx={{ display: { xs: "none", md: "block" } }}>
							<HoursAccordion
								expanded={expanded}
								handleAccordionChange={handleAccordionChange}
							/>
						</Box>
					</Grid2>
				</Grid2>
			</Box>
		</Box>
	);
};

export default Home;
